import * as THREE from 'three';
import { explode, fireRound, tracers, clamp, SOLID, registerStreak, unregisterStreak } from './common.js';

/**
 * Gunship — the top of the ladder, and the only streak that takes the game away
 * from the player and hands them a different one.
 *
 * The aircraft flies a fixed left-hand orbit at altitude. The player does not fly
 * it; they lay the guns, exactly like the real thing and exactly like every
 * gunship in the genre. Mouse walks the aim point across the map, the camera
 * tracks that point from the orbiting airframe, and two weapons are on the
 * triggers: a rapid 25 mm on the left and a 105 mm shell on the right.
 *
 * **Takeover mechanics.** For the duration, `player.dead` is set. That is the one
 * flag the player controller, the weapon system and the enemy AI all already gate
 * on, so a single toggle freezes the body, holsters the weapon, and makes the
 * hostiles stop shooting at a man who is — as far as the fiction is concerned —
 * no longer down there. It is restored on exit, including if the streak is aborted
 * by a state change. Nothing is written to another module's file to achieve it.
 */

export const TUNING = {
  duration: 34,
  /**
   * Orbit geometry. Authored at 92 m / 118 m first, which is the honest altitude
   * for the aircraft and completely unusable in this world: the atmospheric haze
   * is tuned for a street fight, so from up there the whole map washes out to a
   * flat cream rectangle with no readable contacts in it. Sitting lower keeps the
   * ground legible while the shallow look-down angle still reads as "from above".
   *
   * The radius is then deliberately *smaller* than the altitude, for a ~60 degree
   * look-down. A shallower angle puts the horizon and half a kilometre of haze in
   * the top of the frame and a flat cream void in the bottom; steep keeps the
   * whole viewport full of map, which is the only thing the player needs to read.
   */
  altitude: 58,
  orbitRadius: 34,
  orbitSpeed: 0.13, // rad/s — one lap is far longer than the streak
  bankAngle: 0.26,

  /** Pan speed, in metres of ground travel per radian of mouse movement. */
  panScale: 95,
  panDamp: 12,

  /* 25 mm */
  rapidRPS: 7,
  rapidDamage: 55,
  rapidSpread: 0.0055,
  rapidAmmo: 260,
  rapidPower: 0.5,
  rapidRadius: 3.6,
  rapidSplash: 90,

  /* 105 mm */
  shellReload: 2.1,
  shellAmmo: 10,
  shellPower: 3.2,
  shellRadius: 13,
  shellDamage: 420,
  shellFlight: 0.55, // s of shell travel, so the impact lags the report

  /**
   * Enemy silhouettes are marked while the gunship is up — this is a sensor ship.
   * Infinite rather than a large number on purpose: a finite radius means "take a
   * snapshot", which would rebuild the contact set every single frame.
   */
  markRange: Infinity,
};

const _g = new THREE.Vector3();
const _t = new THREE.Vector3();
const _d = new THREE.Vector3();
const _r = new THREE.Vector3();
const _f = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

export class Gunship {
  static id = 'gunship';
  /** The system routes the camera write through `lateUpdate`, on the 'fx' phase. */
  static takeover = true;

  constructor(ctx, sys) {
    this.ctx = ctx;
    this.sys = sys;
    this.ground = new THREE.Vector3();
    this.panVel = new THREE.Vector2();
    this._scratch = [];
    this.active = false;
    // A combatant with a side, so its rockets know whose owner not to hit.
    registerStreak(ctx, this, 'GUNSHIP');
  }

  deploy() {
    const ctx = this.ctx;
    const p = ctx.player;
    if (!p || p.dead) return false;

    this.centre = new THREE.Vector3(p.position.x, 0, p.position.z);
    this.ground.set(p.position.x, p.position.y, p.position.z);
    this.phase = (p.yaw ?? 0) + Math.PI * 0.5;
    this.t = 0;
    this.rapidLeft = TUNING.rapidAmmo;
    this.shellLeft = TUNING.shellAmmo;
    this.rapidTimer = 0;
    this.shellTimer = 0;
    this.shells = [];
    this.panVel.set(0, 0);
    this.active = true;

    /* ---- take the player out of the fight ---- */
    this._wasDead = p.dead;
    p.dead = true;
    this._viewVisible = ctx.viewScene.visible;
    ctx.viewScene.visible = false;
    this.sys.setTakeover(true, Gunship.id);

    ctx.postfx?.flash?.(0.28, 0.5);
    ctx.events.emit('audio:play', { id: 'ui_kill', volume: 1, pitch: 0.55 });
    ctx.events.emit('audio:play', { id: 'explosion', volume: 0.35, pitch: 0.5, delay: 0.05 });
    return true;
  }

  /* --------------------------------- frame -------------------------------- */

  update(dt) {
    this.t += dt;
    if (this.t >= TUNING.duration || (this.rapidLeft <= 0 && this.shellLeft <= 0 && this.shells.length === 0)) {
      this.end();
      return false;
    }

    this._orbit(dt);
    this._pan(dt);
    this._weapons(dt);
    this._flightTime(dt);
    this._mark();
    this.sys.pushHud({
      id: Gunship.id,
      left: Math.max(0, Math.ceil(TUNING.duration - this.t)),
      rapid: this.rapidLeft,
      shells: this.shellLeft,
      reloading: clamp(1 - this.shellTimer / TUNING.shellReload, 0, 1),
    });
    return true;
  }

  _orbit(dt) {
    this.phase += dt * TUNING.orbitSpeed;
    _g.set(
      this.centre.x + Math.cos(this.phase) * TUNING.orbitRadius,
      TUNING.altitude,
      this.centre.z + Math.sin(this.phase) * TUNING.orbitRadius
    );
    this.pos = this.pos ?? new THREE.Vector3();
    this.pos.copy(_g);
  }

  _pan(dt) {
    const input = this.ctx.input;
    // Nobody else is draining the mouse while the player is frozen, so this is the
    // only consumer for the duration.
    const look = input?.consumeMouse?.(1, dt) ?? { yaw: 0, pitch: 0 };

    // Screen-relative panning: yaw walks the point along the camera's right vector,
    // pitch along its forward vector, both flattened onto the ground plane.
    _f.subVectors(this.ground, this.pos);
    _f.y = 0;
    if (_f.lengthSq() < 1e-6) _f.set(0, 0, -1);
    _f.normalize();
    _r.crossVectors(_f, _up).normalize();

    this.panVel.x += -look.yaw * TUNING.panScale;
    this.panVel.y += look.pitch * TUNING.panScale;
    const damp = Math.exp(-TUNING.panDamp * dt);
    this.panVel.multiplyScalar(damp);

    this.ground.addScaledVector(_r, this.panVel.x * dt * TUNING.panDamp);
    this.ground.addScaledVector(_f, this.panVel.y * dt * TUNING.panDamp);

    // Keep the reticle over the playable area and glued to the deck.
    const b = this.ctx.level?.navBounds;
    if (b) {
      this.ground.x = clamp(this.ground.x, b.min.x, b.max.x);
      this.ground.z = clamp(this.ground.z, b.min.z, b.max.z);
    }
    _t.set(this.ground.x, TUNING.altitude, this.ground.z);
    _d.set(0, -1, 0);
    const hit = this.ctx.physics.raycast(_t, _d, TUNING.altitude + 60, SOLID);
    this.ground.y = hit.hit ? hit.point.y : 0;
  }

  /** Camera override. Runs on the 'fx' phase, after the camera rig has had its say. */
  lateUpdate() {
    if (!this.active || !this.pos) return;
    const cam = this.ctx.camera;
    cam.position.copy(this.pos);
    cam.up.set(0, 1, 0);
    cam.lookAt(this.ground);
    // A little bank so the horizon is not dead level — the airframe is turning.
    cam.rotateZ(Math.sin(this.phase * 2) * 0.012 + TUNING.bankAngle * 0.08);
    cam.updateMatrixWorld(true);
  }

  /* -------------------------------- weapons -------------------------------- */

  _weapons(dt) {
    const input = this.ctx.input;
    this.rapidTimer -= dt;
    this.shellTimer -= dt;

    if (input?.fire && this.rapidLeft > 0 && this.rapidTimer <= 0) {
      this.rapidTimer = 1 / TUNING.rapidRPS;
      this._rapid();
    }
    const shellWanted = input?.adsPressed || input?.actionPressed?.('grenade');
    if (shellWanted && this.shellLeft > 0 && this.shellTimer <= 0) {
      this.shellTimer = TUNING.shellReload;
      this._shell();
    }
  }

  _rapid() {
    this.rapidLeft--;
    _d.subVectors(this.ground, this.pos).normalize();
    _d.x += (Math.random() - 0.5) * TUNING.rapidSpread;
    _d.y += (Math.random() - 0.5) * TUNING.rapidSpread;
    _d.z += (Math.random() - 0.5) * TUNING.rapidSpread;
    _d.normalize();

    const start = _t.copy(this.pos).addScaledVector(_d, 4);
    const hit = fireRound(this.ctx, start, _d, 400, TUNING.rapidDamage, { source: this, weapon: 'gunship' });
    const end = hit?.point ?? start.clone().addScaledVector(_d, 300);
    tracers(this.ctx).fire(start, end);

    if (hit?.point) {
      // A 25 mm round is an explosive round; the splash is what makes strafing work.
      explode(this.ctx, hit.point, {
        power: TUNING.rapidPower,
        radius: TUNING.rapidRadius,
        damage: TUNING.rapidSplash,
        playerScale: 0,
        source: this,
        scratch: this._scratch,
      });
    }
    this.ctx.events.emit('audio:play', { id: 'fire_dmr', volume: 0.28, pitch: 0.55 });
  }

  _shell() {
    this.shellLeft--;
    this.shells.push({ t: TUNING.shellFlight, x: this.ground.x, y: this.ground.y, z: this.ground.z });
    this.ctx.events.emit('audio:play', { id: 'fire_shotgun', volume: 0.5, pitch: 0.4 });
  }

  _flightTime(dt) {
    for (let i = this.shells.length - 1; i >= 0; i--) {
      const s = this.shells[i];
      s.t -= dt;
      if (s.t > 0) continue;
      this.shells.splice(i, 1);
      _t.set(s.x, s.y + 0.4, s.z);
      explode(this.ctx, _t, {
        power: TUNING.shellPower,
        radius: TUNING.shellRadius,
        damage: TUNING.shellDamage,
        playerScale: 0,
        source: this,
        scratch: this._scratch,
      });
      // No extra screen flash here: `fx:explosion` already drives the post stack,
      // and stacking a second one on top blew the whole frame out to white.
    }
  }

  /** The gunship's own sensors: everything it can see is painted for the HUD. */
  _mark() {
    this.sys.revealAll(0.4, 'gunship', TUNING.markRange);
  }

  /* --------------------------------- exit ---------------------------------- */

  end() {
    if (!this.active) return;
    this.active = false;
    const ctx = this.ctx;
    const p = ctx.player;
    if (p) p.dead = !!this._wasDead;
    ctx.viewScene.visible = this._viewVisible !== false;
    this.sys.setTakeover(false, Gunship.id);
    this.shells.length = 0;
    ctx.postfx?.flash?.(0.25, 0.4);
    ctx.events.emit('audio:play', { id: 'weapon_switch', volume: 0.8, pitch: 0.7 });
  }

  status() {
    return `${Math.max(0, Math.ceil(TUNING.duration - this.t))}s`;
  }

  dispose() {
    this.end();
    unregisterStreak(this.ctx, this);
  }
}
